import React, { useState } from "react";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import CustomToast from "../../components/misc/CustomToast";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/misc/Card";
import { Button } from "../../components/misc/Button";
import { Input } from "../../components/misc/Input";
import { Switch } from "../../components/misc/Switch";
import { cn } from "../../utils/classMerger";
import { getAppConfigs, resetAppConfig, updateAppConfig } from "./actions";
import { AppConfigItem } from "./types";

interface AppConfigGroupCardProps {
    items: AppConfigItem[];
}

type Draft = string | boolean;

const extractApiError = (err: unknown, fallback: string): string => {
    const axErr = err as AxiosError<{ message?: string | string[] }>;
    const msg = axErr?.response?.data?.message;
    if (Array.isArray(msg)) return msg.join(", ");
    return msg ?? fallback;
};

const errorToast = (message: string): void => {
    toast.error(<CustomToast message={message} type="error" />, { autoClose: 3500 });
};

const isEditable = (item: AppConfigItem): boolean =>
    item.dataType === "NUMBER" || item.dataType === "BOOLEAN" || item.dataType === "STRING";

const toDraft = (item: AppConfigItem): Draft =>
    item.dataType === "BOOLEAN" ? item.value === true : String(item.value ?? "");

const toValue = (item: AppConfigItem, draft: Draft): unknown =>
    item.dataType === "NUMBER" ? Number(draft) : draft;

const isChanged = (item: AppConfigItem, draft: Draft): boolean =>
    item.dataType === "NUMBER" ? Number(draft) !== Number(item.value) : draft !== toDraft(item);

const isAtDefault = (item: AppConfigItem): boolean =>
    JSON.stringify(item.value) === JSON.stringify(item.defaultValue);

const formatValue = (value: unknown): string => {
    if (typeof value === "boolean") return value ? "On" : "Off";
    if (Array.isArray(value)) return value.join(", ");
    return String(value ?? "");
};

const numberError = (item: AppConfigItem, raw: string): string | null => {
    const n = Number(raw);
    if (raw.trim() === "" || !Number.isFinite(n)) return "Enter a number.";
    if (item.integer && !Number.isInteger(n)) return "Enter a whole number.";
    if (item.min !== undefined && n < item.min) return `Must be at least ${item.min}.`;
    if (item.max !== undefined && n > item.max) return `Must be at most ${item.max}.`;
    return null;
};

const draftsFor = (items: AppConfigItem[]): Record<string, Draft> =>
    Object.fromEntries(items.map((item) => [item.key, toDraft(item)]));

const AppConfigGroupCard: React.FC<AppConfigGroupCardProps> = ({ items: initialItems }) => {
    const [items, setItems] = useState<AppConfigItem[]>(initialItems);
    const [drafts, setDrafts] = useState<Record<string, Draft>>(() => draftsFor(initialItems));
    const [busy, setBusy] = useState(false);

    const group = items[0]?.group ?? "";
    const groupLabel = items[0]?.groupLabel || group;
    const editable = items.filter(isEditable);
    const errors = Object.fromEntries(
        editable
            .filter((item) => item.dataType === "NUMBER")
            .map((item) => [item.key, numberError(item, String(drafts[item.key]))]),
    );
    const changed = editable.filter((item) => isChanged(item, drafts[item.key]));
    const invalid = changed.some((item) => errors[item.key]);
    const notAtDefault = editable.filter((item) => !isAtDefault(item));

    const apply = (updated: AppConfigItem[]): void => {
        setItems((prev) => prev.map((item) => updated.find((u) => u.key === item.key) ?? item));
        setDrafts((prev) => ({ ...prev, ...draftsFor(updated) }));
    };

    const reload = async (): Promise<void> => {
        try {
            const fresh = await getAppConfigs(group);
            setItems(fresh);
            setDrafts(draftsFor(fresh));
        } catch (err) {
            errorToast(extractApiError(err, `Failed to reload ${groupLabel} settings`));
        }
    };

    // Each key is its own request; a partial failure leaves the rest saved, so reload afterwards.
    const run = async (
        targets: AppConfigItem[],
        send: (item: AppConfigItem) => Promise<AppConfigItem>,
        success: string,
        fallback: string,
    ): Promise<void> => {
        if (busy || targets.length === 0) return;
        setBusy(true);
        try {
            const results = await Promise.allSettled(targets.map(send));
            const saved = results.flatMap((r) => (r.status === "fulfilled" ? [r.value] : []));
            const failures = results.flatMap((r) => (r.status === "rejected" ? [extractApiError(r.reason, fallback)] : []));
            if (failures.length) {
                errorToast(failures.join(" "));
                await reload();
            } else {
                apply(saved);
                toast.success(<CustomToast message={success} type="success" />, { autoClose: 3000 });
            }
        } finally {
            setBusy(false);
        }
    };

    const handleSave = (): Promise<void> =>
        run(
            changed,
            (item) => updateAppConfig(item.key, toValue(item, drafts[item.key])),
            `${groupLabel} settings saved.`,
            `Failed to save ${groupLabel} settings`,
        );

    const handleReset = (): Promise<void> =>
        run(
            notAtDefault,
            (item) => resetAppConfig(item.key),
            `${groupLabel} settings reset to default.`,
            `Failed to reset ${groupLabel} settings`,
        );

    const renderControl = (item: AppConfigItem, id: string): React.ReactNode => {
        if (item.dataType === "BOOLEAN") {
            return (
                <Switch
                    id={id}
                    aria-label={item.label}
                    checked={drafts[item.key] === true}
                    disabled={busy}
                    onCheckedChange={(checked) => setDrafts((prev) => ({ ...prev, [item.key]: checked }))}
                />
            );
        }
        if (item.dataType === "NUMBER" || item.dataType === "STRING") {
            const isNumber = item.dataType === "NUMBER";
            return (
                <Input
                    id={id}
                    type={isNumber ? "number" : "text"}
                    min={isNumber ? item.min : undefined}
                    max={isNumber ? item.max : undefined}
                    step={isNumber ? (item.integer ? 1 : "any") : undefined}
                    value={String(drafts[item.key])}
                    disabled={busy}
                    aria-invalid={!!errors[item.key]}
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [item.key]: e.target.value }))}
                    className={cn(
                        "text-sm focus-visible:ring-2 focus-visible:ring-primary min-[640px]:w-40",
                        isNumber && "min-[640px]:text-right",
                        errors[item.key] && "border-red-500",
                    )}
                />
            );
        }
        return <span className="text-sm text-gray-700">{formatValue(item.value)}</span>;
    };

    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-left">{groupLabel}</CardTitle>
                {items[0]?.groupDescription && (
                    <p className="text-sm text-gray-500">{items[0].groupDescription}</p>
                )}
            </CardHeader>
            <CardContent className="pt-0">
                <div className="divide-y divide-gray-100">
                    {items.map((item) => {
                        const id = `app-config-${item.key}`;
                        return (
                            <div
                                key={item.key}
                                className="flex flex-col gap-2 py-3 min-[640px]:flex-row min-[640px]:items-start min-[640px]:justify-between min-[640px]:gap-6"
                            >
                                <div className="min-w-0">
                                    <label htmlFor={id} className="text-sm font-semibold text-primary">
                                        {item.label}
                                    </label>
                                    {item.description && (
                                        <p className="text-xs text-gray-500">{item.description}</p>
                                    )}
                                </div>
                                <div className="flex shrink-0 flex-col gap-1 min-[640px]:items-end">
                                    {renderControl(item, id)}
                                    <span className="text-xs text-gray-400">
                                        {isEditable(item)
                                            ? `Default: ${formatValue(item.defaultValue)}`
                                            : "Not editable here"}
                                    </span>
                                    {errors[item.key] && (
                                        <span className="text-xs text-red-500">{errors[item.key]}</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="mt-4 flex flex-col gap-3 border-t border-gray-100 pt-5 min-[480px]:flex-row min-[480px]:items-center">
                    <Button onClick={handleSave} disabled={busy || changed.length === 0 || invalid}>
                        {busy ? "Saving..." : "Save"}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleReset}
                        disabled={busy || notAtDefault.length === 0}
                        className="border-gray-300 text-gray-700"
                    >
                        Reset to default
                    </Button>
                    {changed.length > 0 && (
                        <span className="text-xs text-gray-500">
                            {changed.length} unsaved {changed.length === 1 ? "change" : "changes"}
                        </span>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default AppConfigGroupCard;
